import clsx from 'clsx';
import {
  forwardRef,
  useRef,
  type HTMLAttributes,
  type PropsWithChildren,
} from 'react';

import {
  FontWidthMap,
  charsFontWidth12pixelsSize400Map,
} from './chars-font-width-map.js';
import * as styles from './ChartTextEllipsis.sty.js';
import {
  truncationContainer,
  truncationText,
} from './ChartTextEllipsis.sty.js';
import { useTruncateMiddleEllipsis } from './useTruncateMiddleEllipsis.js';
import { useMergeRefs } from '../../../../core/hooks/useMergeRefs.js';
import type { DataTestId } from '../../../../core/types/data-props.js';
import type { MaskingProps } from '../../../../core/types/masking-props.js';
import type { StylingProps } from '../../../../core/types/styling-props.js';
import type { TruncationMode } from '../../../../typography/text-ellipsis/TextEllipsis.js';

/**
 * The props for the TextEllipsis component.
 * @public
 */
export interface TextEllipsisProps
  extends HTMLAttributes<HTMLDivElement>,
    StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * Pixel width map per character used to compute truncation.
   * Keys are single characters and values are their widths in CSS pixels.
   * Important: the widths must match the effective font family, size, and weight
   * used in the rendered label (e.g., the legend item). If a character is missing,
   * the implementation typically falls back to the width of 'W'.
   *
   * Example shape:
   * { 'A': 7.12, 'B': 7.04, ' ': 3.00, '.': 2.00, ... }
   *
   * @defaultValue charsFontWidth12pixelsSize400Map
   */
  charsFontWidthMap?: FontWidthMap;
  /**
   * The mode used for truncating the text, either at the start, in the middle or at the end.
   * @defaultValue 'end'
   */
  truncationMode?: TruncationMode;
}

const ComputedTextEllipsis = /* @__PURE__ */ forwardRef<
  HTMLSpanElement,
  TextEllipsisProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    charsFontWidthMap,
    truncationMode,
    ...containerProps
  } = props;

  const internalContainerRef = useRef<HTMLDivElement>(null);
  const mergedContainerRef = useMergeRefs<HTMLSpanElement>([
    internalContainerRef,
    forwardedRef,
  ]);

  const text = typeof children === 'string' ? children : String(children);
  const displayText = useTruncateMiddleEllipsis(
    text,
    charsFontWidthMap ?? charsFontWidth12pixelsSize400Map,
    internalContainerRef,
  );

  return (
    <div
      ref={mergedContainerRef}
      className={clsx(truncationContainer, consumerClassName)}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-sourcetext={children}
      style={consumerStyle}
      {...containerProps}
    >
      <span className={truncationText}>{displayText}</span>
    </div>
  );
});

const CssTextEllipsis = /* @__PURE__ */ forwardRef<
  HTMLSpanElement,
  PropsWithChildren<TextEllipsisProps>
>((props, forwardedRef) => {
  const {
    children,
    truncationMode = 'end',
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    charsFontWidthMap, // important to exclude from  remaining props to avoid problems in DOM.
    ...remainingProps
  } = props;

  return (
    <span
      ref={forwardedRef}
      aria-label={String(children)}
      data-testid={dataTestId}
      className={clsx(styles.ellipsis({ truncationMode }), consumerClassName)}
      style={consumerStyle}
      {...remainingProps}
    >
      <span>{children}</span>
    </span>
  );
});

/**
 * @public
 * @param props.charsFontWidthMap - Pixel width map per character (must match the effective font).
 * @param props.truncationMode - Truncation mode: 'start' | 'middle' | 'end' (default 'end').
 */
export const ChartTextEllipsis = /* @__PURE__ */ forwardRef<
  HTMLSpanElement,
  PropsWithChildren<TextEllipsisProps>
>((props, forwardedRef) => {
  const cssOnlyEllipsis = props.truncationMode !== 'middle';

  return cssOnlyEllipsis ? (
    <CssTextEllipsis ref={forwardedRef} {...props} />
  ) : (
    <ComputedTextEllipsis ref={forwardedRef} {...props} />
  );
});

(
  ChartTextEllipsis as typeof ChartTextEllipsis & { displayName: string }
).displayName = 'ChartTextEllipsis';
